import { zodResolver } from '@hookform/resolvers/zod'
import moment from 'moment'
import React from 'react'
import { useForm } from 'react-hook-form'
import { IntlShape, useIntl } from 'react-intl'
import z from 'zod'
import { queryClient } from '../../../../../App'
import { useGetReceiverModels } from '../../../../../hooks/useGetReceiverModels'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { updateInstallationData } from '../../../../../services/ApiService'
import {
  checkEquipmentEverywhere,
  EQUIPMENT_KEYS,
} from '../../../../../services/EquipmentApiService'
import {
  notifyError,
  notifySuccess,
} from '../../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../../utils/controllers/ModalController'
import { getComp } from '../../../../../utils/utils/DI'
import {
  getDefaultValues,
  InstallationDataNewForm,
} from '../../../../RequestsManagement/components/InstalationRequestDrawer/helpers/newInstallation.helpers'
import { getModelId } from '../../../../RequestsManagement/components/InstalationRequestDrawer/hooks/useInstallationDataNew'
import { validateEquipmentId } from '../../../../Service/components/NewDrawers/consts/searchDrawer.consts'
import {
  installationsNotifications,
  installationsNotificationsFull,
} from '../../../consts/notifications.data'
import singleRequestStore from '../../../store/SingleRequestStore'
import { RequestType } from '../../../types/Request.type'
import {
  checkUploadedImages,
  CustomFileType,
} from '../content/DrawerContentInstallation'
import {
  getInstallationDrawerState,
  getParams,
} from '../helpers/installationDrawerHelpers'

const changeInstallationSchema = z.object({
  equipmentId: z.string().refine((val) => validateEquipmentId(val), {
    message: 'Неверный формат id оборудования',
  }),
  receiverSerialNumber: z.string().refine((val) => !val || val.length === 23),
  receiverModelId: z.string(),
  installationDate: z.string().refine(
    (value) => {
      // Преобразуем значение в Moment, если это строка
      const date =
        typeof value === 'string'
          ? moment(value, 'YYYY-MM-DDTHH:mm:ssZ')
          : moment(value) // если уже Moment объект

      return date.isSameOrBefore(moment(), 'day')
    },
    {
      message: 'Дата не должна быть в будущем',
    },
  ),
  smartCardId: z
    .string()
    .nullable()
    .refine((val) => !val, {
      message: 'Ошибка встроенной карты',
    }),
  hasCas: z.boolean(),
})

export function useDrawerContentInstallation(
  request: RequestType,
  onClose: () => void,
) {
  const intl = useIntl()
  const photoSettingsStore = getComp('PhotoSettingsStore')
  const photoPermission = photoSettingsStore._photoSettings['reportedEdit']
  const { setImages, updateHistory, getComments } = singleRequestStore
  // const { goBack } = requestDrawerStore
  const defaultValues = getDefaultValues(request)
  const { data: receiveresModels } = useGetReceiverModels()
  const invalidate = useInvalidateQueries()
  const {
    control,
    watch,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
    trigger,
    setError,
  } = useForm<InstallationDataNewForm>({
    defaultValues,
    resolver: zodResolver(changeInstallationSchema),
    mode: 'onChange',
  })
  const [lastCheckedId, setLastCheckedId] = React.useState(request.equipmentId)
  const [showNotification, setShowNotification] = React.useState(true)
  const [comment, setComment] = React.useState('')
  const [files, setFiles] = React.useState<CustomFileType[] | null>(null)

  const state = getInstallationDrawerState(
    request,
    watch('installationDate'),
    watch('equipmentId'),
    files,
    comment,
  )

  const getBody = (data: InstallationDataNewForm) => {
    const body: Partial<InstallationDataNewForm> = {}
    if (data.equipmentId !== defaultValues.equipmentId)
      body.equipmentId = data.equipmentId
    if (data.installationDate !== defaultValues.installationDate)
      body.installationDate = data.installationDate
    const isReceiverChanged =
      data.receiverModelId !== defaultValues.receiverModelId ||
      data.receiverSerialNumber !== defaultValues.receiverSerialNumber
    if (isReceiverChanged) {
      const modelId = getModelId(data.receiverModelId, receiveresModels)
      if (modelId) {
        body.receiverModelId = modelId
        body.receiverSerialNumber = data.receiverSerialNumber
      }
    }

    return body
  }

  const onSuccess = (res: any, message: string) => {
    const result = checkUploadedImages(res)
    if (!result) {
      setComment('')
    } else {
      setImages(request.id)
      updateHistory()
      getComments(request.id)
      singleRequestStore.updateRequest(res)
      notifySuccess(message)
      onClose()
      invalidate(['get-customer-agreements', 'requests-table'])
    }
  }

  const onSubmit = (data: InstallationDataNewForm) => {
    const filesData = files?.map((file) => file.file) ?? []
    const body = getBody(data)

    const { message } = getParams(
      state,
      data.equipmentId,
      data.installationDate,
      comment,
    )

    openModalFn['progress-backdrop']?.()
    updateInstallationData(request.id, { ...body, ...filesData })
      .then((res) => onSuccess(res, message))
      .finally(() => closeModalFn['progress-backdrop']?.())
  }

  const onSave = () => {
    const { params, message } = getParams(
      state,
      watch('equipmentId'),
      watch('installationDate'),
      comment,
    )
    const filesData = files?.map((file) => file.file) ?? []
    const body =
      request.status === 5 ? { equipmentId: params.equipmentId } : params

    openModalFn['progress-backdrop']?.()
    updateInstallationData(request.id, { ...body, ...filesData })
      .then((res) => onSuccess(res, message))
      .finally(() => closeModalFn['progress-backdrop']?.())
  }

  const onCheckEquipment = async () => {
    const data = await queryClient.fetchQuery({
      queryKey: [EQUIPMENT_KEYS.CHECK_SN_EVERYWHERE, watch('equipmentId')],
      queryFn: () =>
        checkEquipmentEverywhere({ smartCartId: watch('equipmentId') }),
      gcTime: 0,
    })

    if (data?.specs) {
      setValue('hasCas', !!data.specs.hasCas)
      setValue('receiverSerialNumber', data.specs.sn!)
      setValue('receiverModelId', data.specs.model)
    } else {
      setValue('hasCas', false)
    }
    setValue('smartCardId', null)
    setLastCheckedId(watch('equipmentId'))
  }

  React.useEffect(() => {
    if (lastCheckedId && lastCheckedId === watch('equipmentId'))
      setLastCheckedId(defaultValues.equipmentId)
  }, [watch('equipmentId')])

  React.useEffect(() => {
    trigger()
  }, [watch('receiverModelId')])

  React.useEffect(() => {
    if (watch('smartCardId') === watch('equipmentId')) {
      setValue('smartCardId', null)
      setValue('hasCas', true)
      trigger()
    }
  }, [watch('smartCardId')])

  const handleSetFiles = (newFiles: FileList) => {
    const updatedFiles: CustomFileType[] = files ? [...files] : []

    for (let fileNumber in newFiles) {
      const isTooManyPics =
        updatedFiles?.length >= photoPermission.maxLimitPerLoad
      if (isTooManyPics || fileNumber === 'length') return

      if (newFiles[fileNumber].size > photoPermission.maxImageSizeInBytes) {
        const message = getErrorMessage(
          intl,
          photoPermission,
          newFiles,
          fileNumber,
        )
        notifyError(message)
        return
      }

      updatedFiles.push({
        file: newFiles[fileNumber],
        url: URL.createObjectURL(newFiles[fileNumber]),
      })

      setFiles(updatedFiles)
    }
  }

  const deleteFile = (file: CustomFileType) => {
    if (!files) return
    const updatedFiles = files?.filter((f) => f.url !== file.url)

    if (!updatedFiles.length) setFiles(null)
    else setFiles(updatedFiles)
  }

  const isAtLeastOneChanged =
    watch('installationDate') !== defaultValues.installationDate ||
    watch('equipmentId') !== defaultValues.equipmentId ||
    watch('receiverModelId') !== defaultValues.receiverModelId ||
    watch('receiverSerialNumber') !== defaultValues.receiverSerialNumber

  const disableUploadField =
    (files?.length ?? 0) >= photoPermission.maxLimitPerLoad ||
    request.status === 6
  const notifications = state.canChangeInstallationData
    ? installationsNotificationsFull
    : installationsNotifications

  const isEquipmentIdChecked = lastCheckedId === watch('equipmentId')

  return {
    handleSubmit,
    onSubmit,
    control,
    receiveresModels,
    state,
    defaultValues,
    files,
    deleteFile,
    setComment,
    comment,
    intl,
    isValid: isValid && isEquipmentIdChecked,
    isAtLeastOneChanged,
    onSave,
    goBack: onClose,
    disableUploadField,
    photoPermission,
    handleSetFiles,
    showNotification,
    notifications,
    setShowNotification,
    equipmentId: watch('equipmentId'),
    onCheckEquipment,
    setValue,
    watch,
    lastCheckedId,
    errors,
    setError,
  }
}

const getErrorMessage = (
  intl: IntlShape,
  photoPermission: any,
  newFiles: FileList,
  fileNumber: string,
) =>
  `${intl.formatMessage(
    {
      id: 'Размер файла превышает {size} мегабайт',
      defaultMessage: 'Размер файла превышает {size} мегабайт',
    },
    {
      size: photoPermission.maxImageSizeInBytes / 1000000,
    },
  )}: ${newFiles[fileNumber as unknown as number].name}`
